// LiveSessionFlow — lobby → follower → save flow for live sessions (#28).
//
// Renders for HOST and GUESTS alike: with the server's timeline runner as the
// single source of truth, every client is a follower. The flow is:
//   lobby   — ready toggles, substitution check, host start (gated on ready)
//   active  — LiveFollowerAdapter renders server snapshots through the same
//             useWorkoutUiState → WorkoutCoachView pipeline as solo sessions
//   ended   — each participant saves their OWN history record (their plan,
//             their block log) tagged with the shared live_session_id.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  Check,
  Copy,
  Cpu,
  Loader2,
  LogOut,
  Play,
  QrCode,
  Radio,
  Sparkles,
  StopCircle,
  AlertTriangle,
  WifiOff,
  Volume2,
  Pause,
  Plus,
  Share2,
  Smartphone,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useSocial } from "../../contexts/SocialContext";
import { AppStateContext, WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { useToast } from "../../contexts/ToastContext";
import useLiveFollower from "../../hooks/useLiveFollower";
import useLiveHr from "../../hooks/useLiveHr";
import useTelemetryRecorder, { useTelemetryStepMarks } from "../../hooks/useTelemetryRecorder";
import { telemetryPayload } from "../../utils/telemetry/payload";
import { sessionMachineEnergy } from "../../utils/telemetry/energy";
import { heartRateBasis, zoneOf } from "../../utils/heartRateZones";
import useExerciseHistory from "../../hooks/useExerciseHistory";
import useLiveFreeTraining from "../../hooks/useLiveFreeTraining";
import { freeTrainingRecord } from "../../utils/freeTraining";
import { appendFreeBlock, settleFreeBlock, timelineIndexOfChild } from "../../utils/keepGoing";
import useWorkoutUiState, { formatSeconds } from "../../hooks/useWorkoutUiState";
import useWorkoutProfile from "../../hooks/useWorkoutProfile";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import useAudioSettings, { getAudioSettings } from "../../hooks/useAudioSettings";
import useTimerAudioRouting from "../../hooks/useTimerAudioRouting";
import { AUDIO_OUTPUTS, audioOutputLabel, resolveAudioOutput } from "../../audio/audioOutput";
import DetailSheet from "../session/generate/DetailSheet";
import { OptionList } from "../profile/ProfileParts";
import { ensureAudioContext, isAudioUnlocked } from "../../hooks/useSound";
import useMediaSession, { unlockMediaAudio } from "../../hooks/useMediaSession";
import { buildLiveTimeline, rebaseLiveEdit } from "../../utils/liveTimeline";
import { ownTimerToSend } from "../../utils/ownTimers";
import useLocalStorage from "../../hooks/useLocalStorage";
import GoLiveSheet from "./GoLiveSheet";
import {
  buildJoinUrl,
  buildWatchUrl,
  shareOrCopyLink,
} from "../../utils/shareLinks";
import * as api from "../../services/api";
import WorkoutCoachView from "./WorkoutCoachView";
import SessionCompleteModal from "./SessionCompleteModal";
import RepsAdjustSheet from "./RepsAdjustSheet";
import SessionEditDialog from "./SessionEditDialog";
import SessionPlanOutline from "./SessionPlanOutline";
import SwipeablePanes from "./SwipeablePanes";
import QuickSwitch from "./QuickSwitch";
import EquipmentDashboard from "./dashboards/EquipmentDashboard";
import EquipmentSidePanel from "./dashboards/EquipmentSidePanel";
import usePhoneLandscape from "../../hooks/usePhoneLandscape";
import useImmersiveCoach from "../../hooks/useImmersiveCoach";
import { useConnectedEquipment } from "../../contexts/ConnectedEquipmentContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

// ── Participant avatar strip with readiness rings ───────────
const ParticipantAvatars = ({ participants, readyIds, hostId }) => (
  <div className="flex -space-x-2">
    {(participants || []).map((p) => {
      const isReady = readyIds
        ? readyIds.includes(p.id)
        : !!p.isReady;
      const offline = p.connected === false;
      return (
        <div key={p.id} className="relative">
          {p.photo_url ? (
            <img
              src={p.photo_url}
              alt={p.name}
              title={`${p.name}${p.id === hostId ? " (host)" : ""}`}
              className={`h-10 w-10 rounded-full border-2 border-background transition-all ${isReady ? "ring-2 ring-green-500 opacity-100" : offline ? "opacity-30 grayscale" : "opacity-60"}`}
            />
          ) : (
            <div
              title={p.name}
              className={`h-10 w-10 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center text-sm transition-all ${isReady ? "ring-2 ring-green-500 opacity-100" : offline ? "opacity-30 grayscale" : "opacity-60"}`}
            >
              {p.name?.[0]}
            </div>
          )}
          {isReady && (
            <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border-2 border-background">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const noop = () => {};

// ── Lobby ───────────────────────────────────────────────────
const LiveLobby = ({ follower, deviceCast }) => {
  const {
    liveSession,
    leaveLive,
    endLive,
    setLobbyReady,
    startLive,
    sendSessionToDevice,
    presenceByDevice,
  } = useSocial();
  const { user } = useAuth();
  const toast = useToast();
  const { profile } = useUserProfile();
  const historyMap = useExerciseHistory();
  const [workoutSettings] = useWorkoutSettings();
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [watchCopied, setWatchCopied] = useState(false);
  const [starting, setStarting] = useState(false);

  const { session, snapshot, isHost, substitutionStatus, substitutionChanges } =
    liveSession;
  const myPlan = follower.myPlan;

  const participants = snapshot?.participants?.length
    ? snapshot.participants
    : liveSession.participants;
  const me = participants.find((p) => p.id === user?.id);
  const myReady = !!me?.isReady;
  const guests = participants.filter((p) => p.id !== session.host_id);
  // Gate the Start button on CONNECTED guests only — someone who joined and
  // closed the tab must not wedge the lobby (they rejoin mid-session instead).
  const connectedGuests = guests.filter((g) => g.connected !== false);
  const allGuestsReady =
    connectedGuests.length > 0 && connectedGuests.every((g) => g.isReady);

  // Was `${origin}/join/${code}` — a path nothing routes, so a link sent to a
  // mate opened the app on their home screen with the code silently dropped.
  const joinUrl = buildJoinUrl(session.join_code);
  const watchUrl = session.watch_code ? buildWatchUrl(session.watch_code) : null;
  const isBroadcast = session.mode === "broadcast";
  // A broadcast has no participants to invite, so the watch link is THE link
  // and gets the QR code. Otherwise the QR stays what it always was.
  const primaryUrl = isBroadcast ? watchUrl : joinUrl;

  // Starting a live session no longer conscripts your hardware (issue #79),
  // so this is how a wall display gets in on one — the deliberate send, before
  // the clock starts. Hidden unless you actually have a display online that
  // isn't already following; a mate's timer in the session doesn't count.
  const canSendToDevice = ownTimerToSend({
    ownDeviceIds: (deviceCast?.devices || []).map((d) => d.id),
    presenceByDevice,
    connectedDeviceIds: snapshot?.connectedDeviceIds,
  });
  const [sendingToDevice, setSendingToDevice] = useState(false);
  const handleSendToDevice = useCallback(async () => {
    setSendingToDevice(true);
    try {
      await sendSessionToDevice();
      toast.showToast({ title: "Sent to your device" });
    } catch (err) {
      console.error("[live] send to device failed:", err);
      toast.showToast({
        title: "Could not reach your device",
        variant: "destructive",
      });
    } finally {
      setSendingToDevice(false);
    }
  }, [sendSessionToDevice, toast]);

  const toggleReady = useCallback(() => {
    // The Ready tap doubles as the mobile audio unlock so countdown cues can
    // play later without a fresh user gesture.
    ensureAudioContext();
    unlockMediaAudio();
    setLobbyReady(!myReady);
  }, [myReady, setLobbyReady]);

  const handleStart = useCallback(() => {
    if (!myPlan) return;
    setStarting(true);
    ensureAudioContext();
    unlockMediaAudio();
    const timeline = buildLiveTimeline(liveSession.planData, {
      profile,
      historyMap,
      skipLastRest: workoutSettings.skipLastRest ?? true,
    });
    startLive(timeline, {
      countInSeconds: getAudioSettings().countInDuration,
      changeoverHold: workoutSettings.changeoverHold,
    });
  }, [
    myPlan,
    liveSession.planData,
    profile,
    historyMap,
    workoutSettings.skipLastRest,
    workoutSettings.changeoverHold,
    startLive,
  ]);

  return (
    <div className="max-w-lg mx-auto flex flex-col gap-4 p-4">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Radio className="h-4 w-4 text-red-500 animate-pulse" />
          <h2 className="text-xl font-bold">{session.title || "Live session"}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {isBroadcast
            ? "Send the watch link, then start whenever you're ready"
            : isHost
              ? "Waiting for everyone to ready up"
              : "Get set up, then mark yourself ready"}
        </p>
      </div>

      {/* Join code + QR. Hidden entirely for a broadcast: nobody joins one, so
          a join code on screen is an invitation to send the wrong link. */}
      <div className="p-3 rounded-lg bg-muted/30 space-y-2">
        {!isBroadcast && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Join code</p>
              <p className="font-mono text-lg tracking-widest font-bold">
                {session.join_code}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                title="Copy join link"
                onClick={() => {
                  navigator.clipboard?.writeText(joinUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Show QR code"
                onClick={() => setShowQR(!showQR)}
                className={showQR ? "text-primary" : ""}
              >
                <QrCode className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* The link that needs no account at the other end. Called out
            separately from the join link because the two do different things
            and sending the wrong one is exactly the mistake this replaces. */}
        {watchUrl && (
          <div
            className={`flex items-center justify-between gap-2 ${!isBroadcast ? "border-t border-border/50 pt-2" : ""}`}
          >
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                {isBroadcast ? "Watch link" : "Watch link (no account needed)"}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Anyone with this can follow along, read-only.
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                title="Copy watch link"
                onClick={async () => {
                  const outcome = await shareOrCopyLink({
                    url: watchUrl,
                    title: "Watch my workout on Khronos",
                    text: "Follow along live",
                  });
                  if (outcome === "copied") {
                    setWatchCopied(true);
                    setTimeout(() => setWatchCopied(false), 2000);
                  }
                }}
              >
                {watchCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              {isBroadcast && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Show QR code"
                  onClick={() => setShowQR(!showQR)}
                  className={showQR ? "text-primary" : ""}
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {showQR && primaryUrl && (
          <div className="flex justify-center py-2">
            <div className="bg-white p-3 rounded-lg">
              <QRCodeSVG value={primaryUrl} size={160} level="M" bgColor="#ffffff" fgColor="#000000" />
            </div>
          </div>
        )}
      </div>

      {/* Roster with ready states */}
      <div className="flex items-center gap-3">
        <ParticipantAvatars participants={participants} hostId={session.host_id} />
        <span className="text-xs text-muted-foreground">
          {participants.length} in session
          {guests.length > 0 &&
            ` · ${guests.filter((g) => g.isReady).length}/${guests.length} ready`}
        </span>
      </div>

      {/* Guest substitution status */}
      {!isHost && substitutionStatus === "loading" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          Adapting exercises to your equipment…
        </div>
      )}
      {!isHost && substitutionStatus === "done" && substitutionChanges?.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-green-500">
          <Sparkles className="h-4 w-4" />
          {substitutionChanges.length} exercise
          {substitutionChanges.length !== 1 ? "s" : ""} adapted to your equipment
        </div>
      )}
      {!isHost && substitutionStatus === "error" && (
        <div className="flex items-center gap-2 text-sm text-amber-500">
          <AlertTriangle className="h-4 w-4" />
          Couldn't adapt exercises — using the host's plan. Pacing is set by the host.
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col gap-2">
        {!isHost && (
          <Button
            size="lg"
            variant={myReady ? "secondary" : "default"}
            onClick={toggleReady}
            disabled={substitutionStatus === "loading"}
          >
            <Check className="mr-2 h-5 w-5" />
            {myReady ? "Ready ✓ (tap to unready)" : "I'm Ready"}
          </Button>
        )}
        {isHost && (
          <>
            <Button
              size="lg"
              onClick={handleStart}
              disabled={starting || (guests.length > 0 && !allGuestsReady)}
            >
              <Play className="mr-2 h-5 w-5" />
              {starting
                ? "Starting…"
                : guests.length > 0 && !allGuestsReady
                  ? "Waiting for everyone…"
                  : "Start session"}
            </Button>
            {guests.length > 0 && !allGuestsReady && (
              <Button variant="outline" size="sm" onClick={handleStart} disabled={starting}>
                Start anyway
              </Button>
            )}
          </>
        )}
        {canSendToDevice && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendToDevice}
            disabled={sendingToDevice}
          >
            <Cpu className="mr-2 h-4 w-4" />
            {sendingToDevice ? "Sending..." : "Send to device"}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={isHost ? endLive : leaveLive}
        >
          {isHost ? (
            <>
              <StopCircle className="mr-2 h-4 w-4" /> Cancel Session
            </>
          ) : (
            <>
              <LogOut className="mr-2 h-4 w-4" /> Leave Session
            </>
          )}
        </Button>
      </div>

      {myPlan && (
        <SessionPlanOutline plan={myPlan} maxHeight="max-h-[240px]" className="w-full" />
      )}
    </div>
  );
};

// ── Solo channel ready screen ───────────────────────────────
// A solo session channel ("Send to Device") has no lobby semantics — this is
// the pre-workout Ready view. Play can be pressed HERE or on the hardware
// (both send session:cmd start to the server); the snapshot flips everyone
// into the follower adapter together.
const SoloReady = ({ follower, deviceCast }) => {
  const {
    liveSession,
    sendCmd,
    startLive,
    endLive,
    leaveLive,
    createLive,
    sendSessionToDevice,
  } = useSocial();
  const { profile } = useUserProfile();
  const historyMap = useExerciseHistory();
  const [workoutSettings] = useWorkoutSettings();
  const toast = useToast();
  const [starting, setStarting] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const [allowWatchers, setAllowWatchers] = useLocalStorage("live_allow_watchers", false);

  const { session } = liveSession;
  const myPlan = follower.myPlan;
  const deviceOnline = deviceCast?.isOnline !== false;

  // Go live after sending to your timer: the timer channel is private, so
  // swap it for a live session with the same plan and bring the timer along.
  // Nothing has started yet, so the timer just shows the new lobby.
  const goLive = useCallback(
    async (privacy, { mode = "together", allowWatchers: watchers = false } = {}) => {
      const planData = liveSession.planData;
      if (goingLive || !planData) return;
      setGoingLive(true);
      try {
        await endLive();
        leaveLive();
        await createLive({
          title: session.title || planData.label || "Live workout",
          plan_data: planData,
          privacy,
          mode,
          allow_watchers: watchers,
        });
      } catch (err) {
        console.error("[solo] go live failed:", err);
        toast.showToast({
          title: "Couldn't go live",
          description: "Check your connection and try again.",
          variant: "destructive",
        });
        setGoingLive(false);
        return;
      }
      // A broadcast has nobody training with you, but your timer still
      // counts you through it.
      try {
        await sendSessionToDevice({ afterJoin: true });
      } catch (err) {
        console.error("[solo] timer did not follow the live session:", err);
        toast.showToast({
          title: "Live, but your timer didn't follow",
          description: "Use Send to device in the lobby.",
        });
      }
    },
    [goingLive, liveSession.planData, session.title, endLive, leaveLive, createLive, sendSessionToDevice, toast],
  );

  const handleStart = useCallback(async () => {
    if (starting) return;
    setStarting(true);
    ensureAudioContext();
    unlockMediaAudio();
    try {
      // The timeline was staged on the channel at cast time; start without
      // re-uploading. Fall back to a fresh build + live:start if staging was
      // lost (e.g. server restart between cast and play).
      await sendCmd("start");
    } catch (err) {
      try {
        const timeline = buildLiveTimeline(liveSession.planData, {
          profile,
          historyMap,
          skipLastRest: workoutSettings.skipLastRest ?? true,
        });
        startLive(timeline, {
          countInSeconds: getAudioSettings().countInDuration,
          changeoverHold: workoutSettings.changeoverHold,
        });
      } catch (err2) {
        console.error("[solo] start failed:", err2);
        setStarting(false);
      }
    }
  }, [
    starting,
    sendCmd,
    liveSession.planData,
    profile,
    historyMap,
    workoutSettings.skipLastRest,
    workoutSettings.changeoverHold,
    startLive,
  ]);

  return (
    <div className="max-w-lg mx-auto flex flex-col items-center gap-5 p-4">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Cpu className={`h-4 w-4 ${deviceOnline ? "text-cyan-500" : "text-red-400"}`} />
          <h2 className="text-xl font-bold">{session.title || "Workout"}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {deviceOnline
            ? "Sent to your device — start from either screen"
            : "Device offline — it will sync when it reconnects"}
        </p>
      </div>

      <button
        onClick={handleStart}
        disabled={starting}
        aria-label="Start workout"
        className="relative flex items-center justify-center w-[180px] h-[180px]"
      >
        <svg width={180} height={180} viewBox="0 0 180 180" className="absolute inset-0">
          <circle
            cx={90}
            cy={90}
            r={80}
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={10}
          />
        </svg>
        <div className="flex items-center justify-center w-[126px] h-[126px] rounded-full bg-primary/20 hover:bg-primary/30 transition-colors">
          {starting ? (
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
          ) : (
            <Play className="h-14 w-14 text-primary fill-current ml-2" />
          )}
        </div>
      </button>

      {myPlan && (
        <SessionPlanOutline plan={myPlan} maxHeight="max-h-[260px]" className="w-full" />
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => setGoLiveOpen(true)}
        disabled={goingLive || !liveSession.planData}
      >
        {goingLive ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Radio className="mr-2 h-4 w-4" />
        )}
        {goingLive ? "Going live..." : "Go live with mates"}
      </Button>

      <GoLiveSheet
        open={goLiveOpen}
        onOpenChange={setGoLiveOpen}
        goLive={goLive}
        allowWatchers={allowWatchers}
        setAllowWatchers={setAllowWatchers}
      />

      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={async () => {
          // End the channel server-side, then reset local state directly —
          // an unstarted solo session has nothing worth a save prompt.
          try {
            await endLive();
          } finally {
            leaveLive();
          }
        }}
      >
        <StopCircle className="mr-2 h-4 w-4" /> Disconnect device
      </Button>
    </div>
  );
};

// ── Active follower ─────────────────────────────────────────
// ── Where sound plays (issue #45) ───────────────────────────
//
// Shown only while one of your own timers follows the session: with nothing
// attached the choice has nothing to choose between.
const OUTPUT_ICONS = { both: Volume2, app: Smartphone, device: Cpu };

const SoundOutputControl = ({ output, onChange }) => {
  const [open, setOpen] = useState(false);
  const value = resolveAudioOutput(output);
  const Icon = OUTPUT_ICONS[value];
  const label = audioOutputLabel(value);
  return (
    <>
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Sound plays on: ${label}. Change`}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </button>
      </div>
      <DetailSheet open={open} onOpenChange={setOpen} title="Sound plays on">
        <OptionList options={AUDIO_OUTPUTS} value={value} onChange={onChange} />
      </DetailSheet>
    </>
  );
};

const LiveFollowerAdapter = ({ follower, deviceCast, timerAudio, telemetry }) => {
  const {
    liveSession,
    pauseLive,
    resumeLive,
    navigateLive,
    markRepDone,
    forceSkip,
    boostRepCap,
    setRepCapPaused,
    leaveLive,
    endLive,
    detachDevice,
    confirmLiveReady,
    sendSessionToDevice,
    presenceByDevice,
    channelMetrics,
    editLivePlan,
  } = useSocial();
  const { profile } = useUserProfile();
  const historyMap = useExerciseHistory();
  const toast = useToast();
  const { notePeakHeartRate, userProfile: liveProfile } = useContext(WorkoutContext);
  const liveHrBasis = useMemo(() => heartRateBasis(liveProfile), [liveProfile]);
  const phoneLandscape = usePhoneLandscape();
  // Full-screen Coach, as in a session on the phone: this view is only
  // mounted while the session runs, so it enters on mount and leaves on
  // unmount. Without it the app bar and tabs stayed up and a phone on its
  // side had no room for the landscape layout (#149).
  const { isImmersive, setImmersive } = useContext(AppStateContext);
  const { enter: enterImmersive, exit: exitImmersive } = useImmersiveCoach({
    active: true,
    setImmersive,
  });
  const equipment = useConnectedEquipment();
  const [workoutSettings] = useWorkoutSettings();
  const [sessionEditOpen, setSessionEditOpen] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(() => isAudioUnlocked());

  const unlockAudio = useCallback(() => {
    ensureAudioContext();
    unlockMediaAudio();
    setAudioUnlocked(true);
  }, []);

  // Late joiners and "Start anyway" guests reach this active view without the
  // lobby's Ready tap, so their AudioContext was never unlocked and every cue
  // stays silent. Unlock on the first tap anywhere (covers pausing, skipping,
  // etc.); the "Tap to enable sound" banner below is the fallback for a follower
  // who only watches the timer and never taps.
  useEffect(() => {
    if (audioUnlocked) return undefined;
    const onTap = () => unlockAudio();
    document.addEventListener("pointerdown", onTap, { once: true });
    return () => document.removeEventListener("pointerdown", onTap);
  }, [audioUnlocked, unlockAudio]);

  const {
    snapshot,
    isHost,
    myPlan,
    coachState,
    currentNode,
    countInRemaining,
    countInTotal,
    repCapRemaining,
    repCapPaused,
    repCapPausedRemaining,
    isRepBlock,
    hasMarkedDone,
    syncEnabled,
    readyUserIds,
    hostConnected,
    blockIndex,
    phaseLogRef,
    isSubstituted,
    buildSessionPayload,
    getLastRepsSet,
    amendSetReps,
  } = follower;
  useTelemetryStepMarks(telemetry?.mark || noop, coachState);

  // Reps actually done on the set just finished (#144), offered in the rest
  // after it as the offline Coach does (#97). Snapshotted when the sheet
  // opens so the write can't land on a later set.
  const lastRepsSet = getLastRepsSet?.() || null;
  const [repsEdit, setRepsEdit] = useState(null);

  const uiState = useWorkoutUiState({
    coachState,
    workoutPlan: myPlan,
    phaseLogRef,
    countInRemaining,
    countInTotal,
    profile,
    historyMap,
  });

  // NOTE: no device mirroring here — hardware followers attach to the same
  // session channel server-side and receive their own session:state stream.

  // Live HR: direct Web Bluetooth strap is primary; the channel's relayed
  // device metrics (ESP32 BLE HRM via session:metrics) are the fallback.
  // Stale relays (device gone quiet) age out after 5 s.
  const channelBpm =
    channelMetrics?.hr_bpm != null &&
    Date.now() - (channelMetrics.receivedAt || 0) < 5000
      ? channelMetrics.hr_bpm
      : null;
  const { bpm: liveBpm } = useLiveHr(channelBpm);

  // Free training sent to a timer keeps its checklist, log and Rest button.
  const { freeTraining } = useLiveFreeTraining({
    plan: myPlan,
    // Read each render (the follower re-renders on its own tick); the memoised
    // coachState only refreshes when the block or countdown changes.
    elapsed: follower.elapsedSessionTime,
    path: coachState.path,
    blockStart: follower.phaseStartElapsed,
    isRunning: coachState.status === "running",
    bpm: liveBpm,
    hrBasis: liveHrBasis,
    equipment,
  });

  const canPause = isHost || liveSession.allowAnyonePause;
  const canNavigate = isHost || liveSession.allowAnyoneNavigate;
  const isPaused = coachState.status === "paused";
  const isRunning = coachState.status === "running";

  // Lock-screen controls and the silent audio loop that comes with them, as
  // the phone coach has. The loop keeps Chrome from freezing the app when you
  // switch away, which is what dropped the strap mid-session (#185).
  const onMediaPlay = useCallback(() => {
    if (canPause && isPaused) resumeLive();
  }, [canPause, isPaused, resumeLive]);
  const onMediaPause = useCallback(() => {
    if (canPause && isRunning) pauseLive();
  }, [canPause, isRunning, pauseLive]);
  useMediaSession({
    isActive: isRunning || isPaused,
    isPlaying: isRunning,
    exerciseName: currentNode?.label,
    workoutName: myPlan?.label,
    onPlay: onMediaPlay,
    onPause: onMediaPause,
  });

  // Strap HR (and bike power) reach the session channel and the timer from
  // useTimerMetricsRelay, mounted app-wide in App.

  const handleSkipNext = useCallback(() => {
    if (isRepBlock) markRepDone();
    else navigateLive(1, blockIndex);
  }, [isRepBlock, markRepDone, navigateLive, blockIndex]);

  const handleSkipBack = useCallback(() => {
    navigateLive(-1, blockIndex);
  }, [navigateLive, blockIndex]);

  // Drop the device but keep the workout going on screen (solo "Send to Device"
  // sessions only). The server returns the device to idle; we stay in the session.
  const isSolo = liveSession.session?.kind === "solo";
  const handleDetachDevice = useCallback(() => {
    detachDevice();
    toast.showToast({ title: "Device disconnected — finishing here" });
  }, [detachDevice, toast]);

  // ── Edit session (#175) ───────────────────────────────────
  //
  // The host's own session only — solo on a Khronos Timer, or live with people
  // watching. Once a partner joins, each of you may be running a substituted
  // copy of the plan, so an edit to the host's tree would land on the wrong
  // exercises; the server refuses it too.
  const hasPartners = (snapshot?.participants || []).some(
    (p) => Number(p.id) !== Number(liveSession.session?.host_id),
  );
  const canEditSession = isHost && !hasPartners && !!liveSession.planData;
  const block = snapshot?.block;
  const sessionEditPosition = useMemo(
    () =>
      block?.path?.length
        ? { path: block.path, loopCounters: block.loopCounters || {} }
        : null,
    [block],
  );
  const applyLiveEdit = useCallback(
    async (draft) => {
      const { timeline, atIndex, sameBlock } = rebaseLiveEdit(
        liveSession.planData,
        draft,
        block,
        {
          profile,
          historyMap,
          skipLastRest: workoutSettings.skipLastRest ?? true,
        },
      );
      try {
        await editLivePlan(draft, timeline, { atIndex, sameBlock });
        return true;
      } catch (err) {
        console.error("[live] session edit failed:", err);
        toast.showToast({
          title: "Could not apply your changes",
          description:
            err?.message && err.message !== "ack_timeout"
              ? err.message
              : "Check your connection and try again.",
          variant: "destructive",
        });
        return false;
      }
    },
    [liveSession.planData, block, profile, historyMap, workoutSettings.skipLastRest, editLivePlan, toast],
  );

  // ── Pause-screen actions ──────────────────────────────────
  //
  // Both answer the same regret: you are mid-session and wish you had set
  // something up beforehand. Neither is offered on a solo channel, which is
  // already a private wire to your own hardware and has nobody to share with.
  const canSendToDevice =
    !isSolo &&
    ownTimerToSend({
      ownDeviceIds: (deviceCast?.devices || []).map((d) => d.id),
      presenceByDevice,
      connectedDeviceIds: snapshot?.connectedDeviceIds,
    });

  const handleSendToDevice = useCallback(async () => {
    try {
      const { attached } = await sendSessionToDevice();
      toast.showToast(
        attached > 0
          ? { title: "Sent to your device" }
          : {
              title: "No device connected right now",
              description: "It will pick up the session when it next connects.",
            },
      );
    } catch (err) {
      console.error("[live] send to device failed:", err);
      toast.showToast({
        title: "Could not reach your device",
        variant: "destructive",
      });
    }
  }, [sendSessionToDevice, toast]);

  // The watch link where there is one, the join link otherwise: the same
  // precedence the lobby uses, so a broadcast shares what it can actually be
  // shared as.
  const session = liveSession.session;
  const isBroadcastSession = session?.mode === "broadcast";
  const shareUrl = isSolo
    ? null
    : session?.watch_code
      ? buildWatchUrl(session.watch_code)
      : session?.join_code
        ? buildJoinUrl(session.join_code)
        : null;

  const handleShare = useCallback(async () => {
    if (!shareUrl) return;
    const result = await shareOrCopyLink({
      url: shareUrl,
      title: session?.title || "Live session",
      text: "Follow my workout live on Khronos",
    });
    if (result === "copied") toast.showToast({ title: "Link copied" });
    else if (result === "failed") {
      toast.showToast({ title: "Could not share the link", variant: "destructive" });
    }
  }, [shareUrl, session?.title, toast]);

  const pauseActions = [
    ...(canSendToDevice
      ? [{ key: "cast", icon: Cpu, label: "Send to device", onClick: handleSendToDevice }]
      : []),
    ...(shareUrl
      ? [{ key: "share", icon: Share2, label: "Share link", onClick: handleShare }]
      : []),
  ];

  const savePartialAndLeave = useCallback(async () => {
    try {
      const trace = await telemetryPayload(telemetry);
      await api.createSession({ ...buildSessionPayload(), ...trace });
      telemetry?.clearAfterSave().catch(() => {});
      notePeakHeartRate?.(trace.telemetry);
      toast.showToast({ title: "Partial workout saved to history" });
    } catch (err) {
      console.error("[live] partial save failed:", err);
      toast.showToast({ title: "Could not save workout", variant: "destructive" });
    }
    leaveLive();
  }, [buildSessionPayload, leaveLive, toast, telemetry, notePeakHeartRate]);

  if (!uiState || !currentNode) {
    return (
      <div className="w-full max-w-lg mx-auto flex flex-col items-center gap-4 p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-lg font-medium">Syncing session…</p>
      </div>
    );
  }

  // Training alone (broadcast, or nobody joined) releases the block on your
  // own "done", so there is no waiting state to render — and no countdown to
  // extend or freeze.
  const waitingForBarrier =
    isRepBlock && hasMarkedDone && syncEnabled && snapshot?.status === "active";

  // A phone on its side (#149): the bike or rower sits beside the ring
  // rather than a swipe away.
  const hasEquipment = equipment.isSupported && equipment.devices.length > 0;
  const equipmentBeside = hasEquipment && phoneLandscape && !freeTraining;

  const stopActions = isHost
    ? [
        { label: "End session", onClick: endLive },
        ...(isSolo
          ? [{ label: "Disconnect device", onClick: handleDetachDevice, variant: "secondary" }]
          : []),
        { label: "Continue workout", cancel: true },
      ]
    : [
        { label: "Save & leave", onClick: savePartialAndLeave },
        { label: "Leave", onClick: leaveLive, variant: "secondary" },
        { label: "Continue workout", cancel: true },
      ];

  const coachPane = (
    <div className="w-full max-w-lg phone-landscape:max-w-none mx-auto">
      {/* You are live, and here is the proof.
          The lobby says it and a watcher's screen says it, but the host's own
          running session said nothing at all — so a session that failed to go
          live looked exactly like one that succeeded, right up until a solo
          Ready gate appeared in what its owner believed was a broadcast.
          Skipped for a solo channel, which is a private wire to your own
          display rather than anything anyone else can see. */}
      {!isSolo && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 mb-2 text-xs text-red-400 bg-red-500/10 rounded-lg">
          <Radio className="h-3 w-3 animate-pulse" />
          <span>{isBroadcastSession ? "Broadcasting" : "Live"}</span>
        </div>
      )}
      {isSubstituted && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 mb-2 text-xs text-green-500 bg-green-500/10 rounded-lg">
          <Sparkles className="h-3 w-3" />
          <span>Modified for your equipment</span>
        </div>
      )}
      {!hostConnected && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 mb-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg">
          <WifiOff className="h-3 w-3" />
          <span>Host disconnected — session keeps running while they reconnect</span>
        </div>
      )}
      {/* Above the "Tap to enable sound" banner: that one vanishes on the first
          pointerdown, and anything below it would jump out from under the tap. */}
      {timerAudio?.following && (
        <SoundOutputControl output={timerAudio.output} onChange={timerAudio.setOutput} />
      )}
      {!audioUnlocked && (
        <button
          type="button"
          onClick={unlockAudio}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 mb-2 text-xs text-primary bg-primary/10 rounded-lg"
        >
          <Volume2 className="h-3 w-3" />
          <span>Tap to enable sound</span>
        </button>
      )}

      <WorkoutCoachView
        {...uiState}
        // Free training's block is open-ended, so its centre is the session
        // clock counting up, as on the phone.
        {...(freeTraining ? { displayValue: formatSeconds(freeTraining.elapsed) } : {})}
        countInRemaining={countInRemaining}
        countInTotal={countInTotal}
        isReadyGate={coachState.isReadyGate}
        readyGateElapsed={coachState.readyGateElapsed}
        faffTotal={coachState.faffTotal}
        isPaused={isPaused}
        isRunning={isRunning}
        isFinished={false}
        isLast={coachState.isLast}
        onPlayPause={canPause ? (isPaused ? resumeLive : pauseLive) : null}
        onSkipNext={canNavigate || isRepBlock ? handleSkipNext : null}
        onSkipBack={canNavigate ? handleSkipBack : null}
        onFinish={() => (isHost ? setConfirmEnd(true) : setConfirmLeave(true))}
        onConfirmReady={confirmLiveReady}
        pauseActions={pauseActions}
        stopActions={stopActions}
        plan={myPlan}
        currentPath={coachState.path}
        loopCounters={coachState.loopCounters}
        phaseLog={phaseLogRef?.current}
        drainTimer={workoutSettings.drainTimer}
        drainSegments={workoutSettings.drainSegments}
        pauseOnMacroTap={workoutSettings.pauseOnMacroTap ?? true}
        hasSkip={canNavigate || isRepBlock}
        currentBpm={liveBpm}
        hrZone={liveBpm && liveHrBasis ? zoneOf(liveBpm, liveHrBasis) : null}
        freeTraining={freeTraining}
        sidePanel={
          equipmentBeside ? (
            <EquipmentSidePanel
              device={equipment.active}
              hr={equipment.hr}
              hrBpm={liveBpm}
              onAddDevice={() => equipment.addDevice()}
            />
          ) : null
        }
        lastRepsSet={lastRepsSet}
        onEditReps={amendSetReps ? () => setRepsEdit(lastRepsSet) : undefined}
        onEditSession={canEditSession ? () => setSessionEditOpen(true) : undefined}
        isImmersive={isImmersive}
        onEnterImmersive={enterImmersive}
        onExitImmersive={exitImmersive}
      />
      <SessionEditDialog
        open={sessionEditOpen && isPaused}
        onOpenChange={setSessionEditOpen}
        plan={liveSession.planData}
        position={sessionEditPosition}
        onSubmit={applyLiveEdit}
      />
      <RepsAdjustSheet
        open={!!repsEdit}
        onOpenChange={(open) => {
          if (!open) setRepsEdit(null);
        }}
        set={repsEdit}
        onSubmit={amendSetReps}
      />

      {/* Fast-finisher waiting overlay with the rep-cap countdown */}
      {waitingForBarrier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-sm rounded-xl border border-border/50 bg-background p-6 card-glow text-center">
            <h3 className="text-lg font-semibold mb-2">Waiting for others…</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You've finished this set.
              {repCapPaused ? (
                <>
                  {" "}
                  Auto-advance{" "}
                  <span className="font-semibold text-foreground">paused</span>
                  {repCapPausedRemaining != null && (
                    <>
                      {" "}
                      at{" "}
                      <span className="font-mono font-semibold text-foreground">
                        {repCapPausedRemaining}s
                      </span>
                    </>
                  )}
                  .
                </>
              ) : (
                repCapRemaining != null && (
                  <>
                    {" "}
                    Auto-advancing in{" "}
                    <span className="font-mono font-semibold text-foreground">
                      {Math.max(0, repCapRemaining)}s
                    </span>
                    .
                  </>
                )
              )}
            </p>
            <div className="flex justify-center mb-6">
              <ParticipantAvatars
                participants={snapshot?.participants}
                readyIds={readyUserIds}
                hostId={liveSession.session?.host_id}
              />
            </div>
            {/* Anyone can give slower finishers more time, or freeze the cap. */}
            {(repCapRemaining != null || repCapPaused) && (
              <div className="flex gap-2 mb-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setRepCapPaused(!repCapPaused)}
                >
                  {repCapPaused ? (
                    <>
                      <Play className="mr-2 h-4 w-4" /> Resume
                    </>
                  ) : (
                    <>
                      <Pause className="mr-2 h-4 w-4" /> Pause
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={boostRepCap}
                >
                  <Plus className="mr-2 h-4 w-4" /> 30s
                </Button>
              </div>
            )}
            {isHost && (
              <Button variant="outline" className="w-full" onClick={forceSkip}>
                Skip for everyone
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Host end confirmation */}
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent className="border-border/50 card-glow">
          <AlertDialogHeader>
            <AlertDialogTitle>End live session?</AlertDialogTitle>
            <AlertDialogDescription>
              This ends the workout for all participants. Everyone will be
              offered a save to their own history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmEnd(false);
                endLive();
              }}
            >
              End for everyone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Guest leave confirmation (with partial save) */}
      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent className="border-border/50 card-glow">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave the session?</AlertDialogTitle>
            <AlertDialogDescription>
              The group keeps going without you. You can save what you've done
              so far to your history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmLeave(false);
                leaveLive();
              }}
            >
              Leave
            </Button>
            <AlertDialogAction
              onClick={() => {
                setConfirmLeave(false);
                savePartialAndLeave();
              }}
            >
              Save &amp; leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Swipe across to the bike or rower dashboard, as in a session on the
  // phone (#186). It shows whenever a machine is paired.
  if (!hasEquipment || equipmentBeside) return coachPane;

  const activeDevice = equipment.active;
  const equipmentPane = (
    <div className="w-full h-full flex flex-col">
      <QuickSwitch
        devices={equipment.devices}
        activeDeviceId={equipment.activeDeviceId}
        onSelect={equipment.setActive}
        currentPhaseId={currentNode?.id || (coachState?.path ? coachState.path.join(":") : null)}
      />
      <div className="flex-1 min-h-0">
        <EquipmentDashboard
          device={activeDevice}
          onAddDevice={() => equipment.addDevice()}
          hr={equipment.hr}
          hrBpm={liveBpm}
          isPaused={isPaused}
          isRunning={isRunning}
          onPlayPause={canPause ? (isPaused ? resumeLive : pauseLive) : null}
          pauseMenu={{
            stepName: currentNode?.label,
            actions: pauseActions,
            onEditSession: canEditSession ? () => setSessionEditOpen(true) : undefined,
            stopActions,
          }}
          timing={{
            phaseType: uiState.phaseType,
            phaseLabel: uiState.phaseLabel,
            timeLeftInPhase: uiState.isRepBased ? null : coachState.timeLeftInPhase,
            totalRemaining: uiState.totalRemaining,
            nextUpLabel: uiState.nextUpLabel,
          }}
        />
      </div>
    </div>
  );

  return (
    <SwipeablePanes
      className={isImmersive ? "h-[var(--coach-safe-vh)]" : "h-full"}
      indicatorLabels={["Coach", activeDevice?.type === "rower" ? "Rower" : "Bike"]}
    >
      {coachPane}
      {equipmentPane}
    </SwipeablePanes>
  );
};

// ── Watching ────────────────────────────────────────────────
//
// The read-only audience: someone who followed a watch link, quite possibly
// without an account. Deliberately a separate component from
// LiveFollowerAdapter rather than a pile of `!isSpectator &&` conditionals
// inside it — the adapter owns pausing, skipping, rep barriers, HR relay and
// the save-on-leave flow, none of which a watcher may reach, and keeping them
// in one component would make "can a watcher do X?" a question you answer by
// reading four hundred lines rather than by looking at which file it is in.
//
// The server enforces this independently: a spectator socket never has the
// mutating handlers wired to it (api/sockets/live.js). This component is the
// UI half of the same rule, not the rule itself.
const LiveWatchView = ({ follower }) => {
  const { liveSession, leaveLive } = useSocial();
  const { profile } = useUserProfile();
  const historyMap = useExerciseHistory();
  const [workoutSettings] = useWorkoutSettings();
  const [audioUnlocked, setAudioUnlocked] = useState(() => isAudioUnlocked());

  const { snapshot, myPlan, coachState, currentNode, countInRemaining, countInTotal, phaseLogRef } =
    follower;
  const { hostName, session } = liveSession;

  const uiState = useWorkoutUiState({
    coachState,
    workoutPlan: myPlan,
    phaseLogRef,
    countInRemaining,
    countInTotal,
    profile,
    historyMap,
  });

  const unlockAudio = useCallback(() => {
    ensureAudioContext();
    unlockMediaAudio();
    setAudioUnlocked(true);
  }, []);

  const watching = (
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 bg-red-500/10 rounded-lg">
        <Radio className="h-3 w-3 animate-pulse" />
        <span>
          Watching{hostName ? ` ${hostName}` : ""}
        </span>
      </div>
      <Button variant="ghost" size="sm" onClick={leaveLive}>
        <LogOut className="h-3.5 w-3.5 mr-1.5" />
        Stop watching
      </Button>
    </div>
  );

  // Before the host starts, and between "they haven't pressed go" and the
  // first snapshot, there is genuinely nothing to render but the plan.
  const status = snapshot?.status || session?.status;
  if (status === "lobby" || status === "waiting" || !uiState || !currentNode) {
    return (
      <div className="w-full max-w-lg mx-auto p-4">
        {watching}
        <div className="rounded-lg border border-border p-6 text-center space-y-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
          <p className="font-medium">
            {hostName ? `${hostName} hasn't started yet` : "Not started yet"}
          </p>
          <p className="text-sm text-muted-foreground">
            This page will follow along automatically once they begin.
          </p>
        </div>
        {myPlan && (
          <div className="mt-4 rounded-lg border border-border p-2 overflow-x-auto">
            <SessionPlanOutline plan={myPlan} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {watching}

      {/* Opt-in, never automatic: a page someone opened to watch a mate must
          not start beeping at them unprompted. */}
      {!audioUnlocked && (
        <button
          type="button"
          onClick={unlockAudio}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 mb-2 text-xs text-primary bg-primary/10 rounded-lg"
        >
          <Volume2 className="h-3 w-3" />
          <span>Tap to follow the sound cues</span>
        </button>
      )}

      <WorkoutCoachView
        {...uiState}
        countInRemaining={countInRemaining}
        countInTotal={countInTotal}
        isReadyGate={coachState.isReadyGate}
        readyGateElapsed={coachState.readyGateElapsed}
        faffTotal={coachState.faffTotal}
        isPaused={coachState.status === "paused"}
        isRunning={coachState.status === "running"}
        isFinished={false}
        isLast={coachState.isLast}
        readOnly
        onPlayPause={null}
        onSkipNext={null}
        onSkipBack={null}
        onFinish={null}
        hasSkip={false}
        plan={myPlan}
        currentPath={coachState.path}
        loopCounters={coachState.loopCounters}
        phaseLog={phaseLogRef?.current}
        drainTimer={workoutSettings.drainTimer}
        drainSegments={workoutSettings.drainSegments}
        pauseOnMacroTap={false}
      />
    </div>
  );
};

// The workout is over, or the link was opened after it ended.
const LiveWatchEnded = () => {
  const { liveSession, leaveLive } = useSocial();
  const { hostName, session } = liveSession;

  return (
    <div className="w-full max-w-lg mx-auto p-4">
      <div className="rounded-lg border border-border p-6 text-center space-y-3">
        <Check className="h-8 w-8 text-green-500 mx-auto" />
        <p className="text-lg font-semibold">Session finished</p>
        <p className="text-sm text-muted-foreground">
          {hostName
            ? `${hostName} has finished ${session?.title ? `"${session.title}"` : "this workout"}.`
            : "This workout has finished."}
        </p>
        <Button variant="outline" onClick={leaveLive}>
          Done
        </Button>
      </div>
    </div>
  );
};

// ── Ended → per-participant save ────────────────────────────
const LiveEndedSave = ({ follower, telemetry }) => {
  const { liveSession, leaveLive, extendLive } = useSocial();
  const { activeProgrammeSessionRef, notePeakHeartRate } = useContext(WorkoutContext);
  const { profile } = useUserProfile();
  const historyMap = useExerciseHistory();
  const [workoutSettings] = useWorkoutSettings();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { session, isHost } = liveSession;
  const free = useLiveFreeTraining({
    plan: follower.myPlan,
    elapsed: follower.elapsedSessionTime,
    isRunning: false,
  });
  // A Free training block kept going into (#199) is saved with what was
  // logged in it as its steps.
  const servedInLastBlock = Math.max(
    0,
    follower.elapsedSessionTime - (follower.phaseStartElapsed || 0),
  );
  const plan = useMemo(
    () => settleFreeBlock(follower.myPlan, free.freeLog, servedInLastBlock),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [follower.myPlan, free.freeLog],
  );

  // Keep going (#199): the host carries this session on with a Free training
  // block at the end, on the same clock, timer and share. A whole Free
  // training session has nothing to add to.
  const canKeepGoing = isHost && !free.freeTraining && !!plan?.children;
  const keepGoing = async () => {
    const { plan: next, index } = appendFreeBlock(plan);
    const timeline = buildLiveTimeline(next, {
      profile,
      historyMap,
      skipLastRest: workoutSettings.skipLastRest ?? true,
    });
    try {
      await extendLive(next, timeline, timelineIndexOfChild(timeline, index));
      free.reset();
      return true;
    } catch (err) {
      console.error("[live] keep going failed:", err);
      toast.showToast({
        title: "Couldn't keep going",
        description:
          err?.message && err.message !== "ack_timeout"
            ? err.message
            : "Check your connection and try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleSave = async (feedback) => {
    if (saving) return;
    setSaving(true);
    try {
      const trace = await telemetryPayload(telemetry);
      // Free training is filed as the activity picked at the end, with what
      // was logged as its plan, as on the phone (#146, #157).
      const freeRecord = free.freeTraining
        ? freeTrainingRecord({
            activityType: feedback?.activityType,
            log: free.freeLog,
            plan: feedback?.freePlan,
          })
        : null;
      const saved = await api.createSession({
        ...follower.buildSessionPayload(feedback, { plan }),
        ...(freeRecord || {}),
        ...trace,
      });
      free.reset();
      telemetry?.clearAfterSave().catch(() => {});
      notePeakHeartRate?.(trace.telemetry);
      toast.showToast({ title: "Workout saved to history" });
      // A programme session run via live/device must mark its slot complete,
      // exactly as the solo coach path does (useWorkoutCoach).
      const prog = activeProgrammeSessionRef?.current;
      if (prog && saved?.id) {
        try {
          await api.completeProgrammeSession(
            prog.programmeId,
            prog.programmeSessionId,
            {
              workout_session_id: saved.id,
              duration_minutes: Math.max(
                1,
                Math.round((follower.elapsedSessionTime || 60) / 60),
              ),
            },
          );
          activeProgrammeSessionRef.current = null;
        } catch (err) {
          console.error("[live] programme link failed:", err);
        }
      }
    } catch (err) {
      console.error("[live] session save failed:", err);
      toast.showToast({ title: "Could not save workout", variant: "destructive" });
    }
    leaveLive();
  };

  // The server auto-records a fallback history row for the host at session
  // end (crash safety). Host "Discard" deletes it; a guest's "Leave" has no
  // row to remove.
  const handleSkip = async () => {
    if (saving) return;
    if (isHost && session?.id) {
      setSaving(true);
      try {
        await api.discardLiveHistory(session.id);
        toast.showToast({ title: "Session discarded" });
      } catch (err) {
        console.error("[live] history discard failed:", err);
        toast.showToast({
          title: "Could not discard the auto-saved session",
          variant: "destructive",
        });
      }
    }
    telemetry?.discard().catch(() => {});
    free.reset();
    leaveLive();
  };

  return (
    <SessionCompleteModal
      workoutPlan={plan}
      machineEnergy={sessionMachineEnergy(telemetry?.samplesRef?.current)}
      elapsedSessionTime={follower.elapsedSessionTime}
      repsSets={follower.getDoneRepsSets?.()}
      onAmendReps={follower.amendSetReps}
      onSave={handleSave}
      onKeepGoing={canKeepGoing ? keepGoing : null}
      onSkip={handleSkip}
      skipLabel={isHost ? "Discard" : "Leave"}
      freeLog={free.freeLog}
      freeChecklist={free.freeChecklist}
      exerciseList={free.exerciseList}
      onFreeLogChange={free.updateLog}
    />
  );
};

// ── Flow root ───────────────────────────────────────────────
const LiveSessionFlow = ({ deviceCast }) => {
  const { liveSession, substituteExercises } = useSocial();
  const { userProfile } = useWorkoutProfile();
  const follower = useLiveFollower();

  const { session, snapshot, isHost, planData, substitutionStatus, isSpectator } =
    liveSession;

  // Your own timers following this session — a mate's timer in a shared
  // session is theirs to mute. Watchers never route sound to hardware.
  const [audio, updateAudio] = useAudioSettings();
  const ownIds = new Set((deviceCast?.devices || []).map((d) => Number(d.id)));
  const ownAttached = (snapshot?.connectedDeviceIds || [])
    .map(Number)
    .filter((id) => ownIds.has(id));
  const sessionEnded =
    snapshot?.status === "ended" || session?.status === "finished";
  useTimerAudioRouting(ownAttached, audio.output, !!session && !isSpectator && !sessionEnded);
  const timerAudio = {
    following: ownAttached.length > 0,
    output: audio.output,
    setOutput: (output) => updateAudio({ output }),
  };

  // Record the strap and bike at 1 Hz while the session runs, as the phone
  // coach does, so a session on the Khronos Timer or gone live keeps its
  // charts (#188). Watchers aren't training, so they don't record.
  const { userProfile: zoneProfile } = useContext(WorkoutContext);
  const hrBasis = useMemo(() => heartRateBasis(zoneProfile), [zoneProfile]);
  const runStatus = snapshot?.status || session?.status;
  const recording =
    !!session && !isSpectator && !sessionEnded &&
    runStatus !== "lobby" && runStatus !== "waiting";
  // Keyed to the session, so opening another tab and coming back carries on
  // the same recording rather than starting a second one.
  const telemetry = useTelemetryRecorder({
    active: recording,
    hrBasis,
    persistKey: session?.id != null ? `live:${session.id}` : null,
  });
  // The phone coach marks its workout as running; do the same here so bike
  // and rower summaries collect and dropped gear keeps being retried.
  // Held in a ref: a new setter identity mustn't flip the flag off and on,
  // which would reset the bike's session summary.
  const { setWorkoutActive } = useConnectedEquipment();
  const setWorkoutActiveRef = useRef(setWorkoutActive);
  setWorkoutActiveRef.current = setWorkoutActive;
  // Kept on at the finish: turning it back on for Keep going (#199) would
  // start the bike's totals again.
  const equipmentActive = recording || (!!session && !isSpectator && sessionEnded);
  useEffect(() => {
    setWorkoutActiveRef.current(equipmentActive);
    return () => setWorkoutActiveRef.current(false);
  }, [equipmentActive]);

  // Trigger AI equipment substitution on join — here rather than the lobby UI
  // so LATE joiners (who skip the lobby) get it too. Non-blocking: the
  // follower shows host labels until localPlanData updates.
  //
  // Watchers are excluded: substitution rewrites the plan for the equipment
  // *you* have, which is meaningless when you are watching someone else work
  // out, and it would need a profile a signed-out watcher does not have.
  useEffect(() => {
    if (isHost || isSpectator || !planData || substitutionStatus) return;
    const eq =
      typeof userProfile?.availableEquipment === "string"
        ? userProfile.availableEquipment.split(",").map((e) => e.trim())
        : userProfile?.availableEquipment;
    if (eq?.length) substituteExercises(eq);
  }, [isHost, isSpectator, planData, substitutionStatus, userProfile?.availableEquipment, substituteExercises]);

  // Mobile browsers suspend the AudioContext on tab switches; resume on
  // return so countdown cues keep working.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") ensureAudioContext();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  if (!session) return null;

  const status = snapshot?.status || (session.status === "waiting" ? "lobby" : session.status);

  // Watchers branch first and never fall through: every arm below this assumes
  // a participant — a roster seat, a ready toggle, a history record to save.
  if (isSpectator) {
    if (status === "ended" || session.status === "finished") {
      return <LiveWatchEnded />;
    }
    return <LiveWatchView follower={follower} />;
  }

  if (status === "ended" || session.status === "finished") {
    return <LiveEndedSave follower={follower} telemetry={telemetry} />;
  }
  if (status === "lobby" || status === "waiting") {
    // Solo channels skip the social lobby: no roster, no join code, just a
    // Ready screen mirrored on the hardware.
    if (session.kind === "solo") {
      return <SoloReady follower={follower} deviceCast={deviceCast} />;
    }
    return <LiveLobby follower={follower} deviceCast={deviceCast} />;
  }
  return (
    <LiveFollowerAdapter
      follower={follower}
      deviceCast={deviceCast}
      timerAudio={timerAudio}
      telemetry={telemetry}
    />
  );
};

export default LiveSessionFlow;
